import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import {
  Globe,
  Search,
  Palette,
  Euro,
  FileText,
  Image,
  Users,
  Megaphone,
  Settings,
  MessageCircle,
  Star,
  FileSignature,
  Link2,
  Monitor,
  Briefcase,
  Shield,
  ArrowLeft
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

// =============================================
// ADMIN PORTAL - Accès rapide à toutes les sections
// =============================================

const AdminPage = () => {
  const { isAgency } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);

  // Redirect non-agency users
  if (!isAgency) {
    navigate('/dashboard/agency');
    return null;
  }

  const adminSections = [
    {
      title: 'Site Vitrine',
      description: 'Gérez le contenu de votre site web',
      color: 'from-blue-500 to-cyan-500',
      items: [
        { to: '/admin/home', icon: Globe, label: 'Page d\'accueil', description: 'Hero, stats, services' },
        { to: '/admin/pages', icon: FileText, label: 'Pages du site', description: 'Autres pages' },
        { to: '/admin/portfolio', icon: Briefcase, label: 'Portfolio', description: 'Vos réalisations' },
        { to: '/admin/case-studies', icon: FileText, label: 'Études de cas', description: 'Références clients' },
        { to: '/admin/team', icon: Users, label: 'Équipe', description: 'Présentez votre équipe' },
        { to: '/admin/gallery', icon: Image, label: 'Galerie clients', description: 'Logos de confiance' },
      ]
    },
    {
      title: 'Marketing & SEO',
      description: 'Optimisez votre référencement',
      color: 'from-emerald-500 to-teal-500',
      items: [
        { to: '/admin/seo', icon: Search, label: 'SEO & Pages villes', description: 'Référencement local' },
        { to: '/admin/news', icon: Megaphone, label: 'Actualités', description: 'Blog et news' },
        { to: '/admin/reviews', icon: Star, label: 'Avis Google', description: 'Synchroniser les avis' },
        { to: '/admin/promo-banner', icon: Megaphone, label: 'Bandeau promo', description: 'Promotions clients' },
      ]
    },
    {
      title: 'Configuration',
      description: 'Paramètres de l\'application',
      color: 'from-amber-500 to-orange-500',
      items: [
        { to: '/admin/pricing', icon: Euro, label: 'Tarification & Packs', description: 'Prix et forfaits' },
        { to: '/admin/theme', icon: Palette, label: 'Thème & Couleurs', description: 'Personnalisation' },
        { to: '/admin/header', icon: Monitor, label: 'Bandeau & Horaires', description: 'Header du site' },
        { to: '/admin/legal', icon: FileSignature, label: 'Pages légales', description: 'Mentions, CGV...' },
        { to: '/admin/settings', icon: Settings, label: 'Paramètres', description: 'Configuration générale' },
        { to: '/admin/demo-mode', icon: Monitor, label: 'Mode Démo', description: 'Données de démonstration' },
      ]
    },
    {
      title: 'Intégrations',
      description: 'Connectez vos outils',
      color: 'from-violet-500 to-purple-500',
      items: [
        { to: '/settings/smtp', icon: MessageCircle, label: 'Email (SMTP)', description: 'Configuration email' },
        { to: '/settings/integrations/axonaut', icon: Link2, label: 'Axonaut', description: 'Facturation' },
        { to: '/settings/templates', icon: FileText, label: 'Modèles documents', description: 'Devis, factures' },
      ]
    },
  ];

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 px-6 md:px-8 pb-6 md:pb-8 pt-32 md:pt-32",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}
        style={{ paddingTop: "130px" }}
      >
        <div className="max-w-6xl mx-auto">
          {/* Header with badge */}
          <div className="mb-8 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Administration</h1>
                <p className="text-slate-500">Gérez votre site et vos paramètres</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => navigate('/dashboard/agency')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour Espace Client
            </Button>
          </div>

          <div className="grid gap-8">
            {adminSections.map((section, idx) => (
              <div key={idx}>
                <div className="flex items-center gap-3 mb-4">
                  <div className={cn("w-1 h-8 rounded-full bg-gradient-to-b", section.color)} />
                  <div>
                    <h2 className="text-lg font-bold text-slate-800">{section.title}</h2>
                    <p className="text-sm text-slate-500">{section.description}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {section.items.map((item, itemIdx) => {
                    const Icon = item.icon;
                    return (
                      <Card 
                        key={itemIdx}
                        className="bg-white hover:shadow-lg transition-all cursor-pointer border border-slate-200 group"
                        onClick={() => navigate(item.to)}
                      >
                        <CardContent className="p-4">
                          <div className={cn(
                            "w-10 h-10 rounded-xl flex items-center justify-center mb-3 bg-gradient-to-br",
                            section.color,
                            "group-hover:scale-110 transition-transform"
                          )}>
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <h3 className="font-semibold text-slate-800 mb-1">{item.label}</h3>
                          <p className="text-xs text-slate-500">{item.description}</p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminPage;
